import {
  Card,
  FSRS,
  Grade,
  RecordLog,
  State,
  createEmptyCard,
  generatorParameters,
  FSRSParameters
} from 'ts-fsrs'
import { CardEntity } from './types'

export class FSRSScheduler {
  private fsrs: FSRS

  constructor(deckParams?: FSRSParameters | null) {
    // Cast strict type to ts-fsrs compatible partial
    const safeParams = deckParams
      ? {
          request_retention: deckParams.request_retention,
          maximum_interval: deckParams.maximum_interval,
          w: deckParams.w,
          enable_fuzz: deckParams.enable_fuzz
        }
      : undefined

    const params = generatorParameters(safeParams)
    this.fsrs = new FSRS(params)
  }

  /**
   * 1. Convert DB Row -> FSRS Card Object
   */
  private mapEntityToFSRS(entity: CardEntity): Card {
    return {
      due: new Date(entity.due),
      stability: entity.stability ?? 0,
      difficulty: entity.difficulty ?? 0,
      elapsed_days: entity.elapsed_days ?? 0,
      scheduled_days: entity.scheduled_days ?? 0,
      learning_steps: entity.learning_steps ?? 0,
      reps: entity.reps ?? 0,
      lapses: entity.lapses ?? 0,
      state: (entity.state as State) ?? State.New,
      last_review: entity.last_review ? new Date(entity.last_review) : undefined
    }
  }

  private repeat(entity: CardEntity, now = new Date()): RecordLog {
    const card = this.mapEntityToFSRS(entity)
    return this.fsrs.repeat(card, now)
  }

  /**
   * 2. Calculate next intervals
   */
  getRecordLog(entity: CardEntity, now = new Date()) {
    return this.repeat(entity, now)
  }

  /**
   * 3. Process a review
   */
  processReview(entity: CardEntity, rating: Grade, now = new Date()) {
    const schedulingCards = this.repeat(entity, now)
    const result = schedulingCards[rating]

    return {
      cardUpdate: {
        due: result.card.due.toISOString(),
        stability: result.card.stability,
        difficulty: result.card.difficulty,
        elapsed_days: result.card.elapsed_days,
        scheduled_days: result.card.scheduled_days,
        learning_steps: result.card.learning_steps,
        reps: result.card.reps,
        lapses: result.card.lapses,
        state: result.card.state,
        last_review: result.card.last_review?.toISOString() || now.toISOString()
      },
      logInsert: {
        card_id: entity.id,
        user_id: entity.user_id,
        rating: rating,
        state: entity.state,
        due: entity.due,
        stability: entity.stability,
        difficulty: entity.difficulty,
        elapsed_days: result.log.elapsed_days,
        last_elapsed_days: entity.elapsed_days,
        scheduled_days: result.log.scheduled_days,
        learning_steps: result.log.learning_steps,
        review_time: result.log.review.toISOString()
      }
    }
  }

  formatDueLabel(due: string | Date, now = new Date()): string {
    const dueDate = typeof due === 'string' ? new Date(due) : due
    const diffMs = dueDate.getTime() - now.getTime()
    if (diffMs <= 0) return 'now'

    const minutes = Math.round(diffMs / 60000)
    if (minutes < 60) return `${minutes}m`

    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours}h`

    const days = Math.round(hours / 24)
    return `${days}d`
  }

  createNewCard(
    userId: string,
    noteId: string | null | undefined,
    front: string,
    back: string
  ): Partial<CardEntity> {
    const empty = createEmptyCard()
    return {
      user_id: userId,
      note_id: noteId ?? '',
      template_key: 'recognition',
      front,
      back,
      card_data: {},
      state: empty.state,
      due: empty.due.toISOString(),
      stability: empty.stability,
      difficulty: empty.difficulty,
      elapsed_days: empty.elapsed_days,
      scheduled_days: empty.scheduled_days,
      learning_steps: empty.learning_steps,
      reps: empty.reps,
      lapses: empty.lapses
    }
  }
}
